import * as R from 'ramda';
import { sanitizeJobLog, SUPPORTED_JOB_STATUSES } from '../gondolaJobStatus';

const PROGRESS_MARKER = /^(?:\[GONDOLA_PROGRESS\]\s*(\{.*\})|(?:\d+(?:\.\d+)?) - (?:DEBUG|INFO|WARNING|ERROR|CRITICAL) - (?:\[scopeId=[^\]\r\n]*\]\s*)?(?:\[companyId=[^\]\r\n]*\]\s*)?\[GONDOLA_PROGRESS\]\s*(\{.*\}))\s*$/;
const PROGRESS_COUNT_FIELDS = ['currentIteration', 'maxIterations', 'processedCount', 'totalCount', 'lastBookingDurationMs'];
const PROGRESS_TEXT_FIELDS = ['currentBookingId', 'currentBookingReference', 'lastBookingId', 'lastBookingReference'];
const PROGRESS_TIMESTAMP_FIELDS = ['heartbeatAt', 'currentBookingStartedAt'];
const PROGRESS_PHASES = ['iteration_evaluation', 'full_evaluation'];
const MAX_PROGRESS_COUNT = 10 * 1000 * 1000;
const MAX_PROGRESS_COST = 1000 * 1000;
const MAX_HEARTBEAT_FUTURE_SKEW_MS = 5 * 60 * 1000;
const UTC_ISO_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(?:Z|\+00:00)$/;

const parseCanonicalUtcTimestamp = value => {
  const match = value.match(UTC_ISO_TIMESTAMP);
  if (!match) return NaN;
  const [, yearText, monthText, dayText, hourText, minuteText, secondText, fraction = ''] = match;
  const [year, month, day, hour, minute, second] = [
    yearText, monthText, dayText, hourText, minuteText, secondText,
  ].map(Number);
  const millisecond = Number(`${fraction}000`.slice(0, 3));
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hour, minute, second, millisecond);
  if (date.getUTCFullYear() !== year
      || date.getUTCMonth() !== month - 1
      || date.getUTCDate() !== day
      || date.getUTCHours() !== hour
      || date.getUTCMinutes() !== minute
      || date.getUTCSeconds() !== second) {
    return NaN;
  }
  return date.getTime();
};

const sanitizeProgress = parsed => {
  const progress = {};
  PROGRESS_COUNT_FIELDS.forEach(key => {
    if (Number.isSafeInteger(parsed[key])
        && parsed[key] >= 0
        && parsed[key] <= MAX_PROGRESS_COUNT) {
      progress[key] = parsed[key];
    }
  });
  if (Number.isFinite(parsed.lastIterationAccuracy)
      && parsed.lastIterationAccuracy >= 0 && parsed.lastIterationAccuracy <= 1) {
    progress.lastIterationAccuracy = parsed.lastIterationAccuracy;
  }
  if (Number.isFinite(parsed.costSoFar)
      && parsed.costSoFar >= 0
      && parsed.costSoFar <= MAX_PROGRESS_COST) {
    progress.costSoFar = parsed.costSoFar;
  }
  PROGRESS_TEXT_FIELDS.forEach(key => {
    if (parsed[key] === null) progress[key] = null;
    if (typeof parsed[key] === 'string') progress[key] = parsed[key].slice(0, 256);
  });
  PROGRESS_TIMESTAMP_FIELDS.forEach(key => {
    if (typeof parsed[key] === 'string' && parsed[key].length <= 32) {
      const timestamp = parseCanonicalUtcTimestamp(parsed[key]);
      if (Number.isFinite(timestamp)
          && timestamp <= Date.now() + MAX_HEARTBEAT_FUTURE_SKEW_MS) {
        progress[key] = parsed[key];
      }
    }
  });
  if (PROGRESS_PHASES.includes(parsed.phase)) progress.phase = parsed.phase;
  return progress;
};

const parseProgressMarker = log => {
  if (!Array.isArray(log)) return null;
  let progress = null;
  for (let i = 0; i < log.length; i += 1) {
    const match = typeof log[i] === 'string' && log[i].match(PROGRESS_MARKER);
    if (match) {
      try {
        const parsed = JSON.parse(match[1] || match[2]);
        progress = { ...(progress || {}), ...sanitizeProgress(parsed) };
      } catch (_) { /* ignore malformed progress markers */ }
    }
  }
  return progress;
};

const isMatchingCurrentScope = (state, contextScope) => {
  const currentScope = state.currentScope;
  if (!currentScope) return false;
  if (!contextScope) return true;
  if (typeof contextScope !== 'string') return false;
  if (currentScope.contextScope) return currentScope.contextScope === contextScope;
  return currentScope.scopeId === contextScope.split('/').pop();
};

const getArgsContextScope = args => {
  if (!Array.isArray(args)) return null;
  const metadata = args.find(arg => arg && typeof arg === 'object' && arg.contextScope);
  return metadata ? metadata.contextScope : null;
};

const shouldRejectScopeAction = (state, args) => {
  const actionContextScope = getArgsContextScope(args);
  const currentContextScope = R.path(['currentScope', 'contextScope'], state);
  if (currentContextScope && !actionContextScope) return true;
  return Boolean(actionContextScope && !isMatchingCurrentScope(state, actionContextScope));
};

const getActionCompanyId = (args, payload) => {
  if (Array.isArray(args)) {
    const owner = args.find(arg => arg && typeof arg === 'object' && typeof arg.companyId === 'string');
    if (owner) return owner.companyId;
    if (typeof args[0] === 'string') return args[0];
  }
  return payload && typeof payload.companyId === 'string' ? payload.companyId : null;
};

const shouldRejectCompanyAction = (state, args, payload) => {
  const companyId = getActionCompanyId(args, payload);
  return Boolean(companyId && state.accountCompanyId && companyId !== state.accountCompanyId);
};

const getScopeRequestKey = (args, identityArgCount) => {
  if (!Array.isArray(args)) return null;
  return JSON.stringify([
    ...args.slice(0, identityArgCount),
    getArgsContextScope(args),
  ]);
};

const sanitizeJobText = (value, maxLength = 1000) => (
  typeof value === 'string' ? value.slice(0, maxLength) : null
);

const SAFE_JOB_ALERT_TYPES = new Set([
  'nx_credentials_expired',
  'reporting_incomplete',
  'externalSessionRequired',
  'externalSessionInvalid',
]);

const EMPTY_CURRENT_SCOPE = {
  detailLoading: false,
  runs: [],
  runsLoading: false,
  runsNextCursor: null,
  files: [],
  filesLoading: false,
  selectedFileName: null,
  selectedFileContent: null,
  selectedFileDownloadUrl: null,
  selectedFileS3Path: null,
  selectedFileAccess: null,
  selectedFileSize: null,
  selectedFileLoading: false,
  savingFile: false,
  scopeFileRequestKey: null,
  associations: [],
  associationsLoading: false,
  ti2s: [],
  ti2Source: null,
  countsLoading: false,
  liveRun: null,
};

const RUN_START_ERROR_MESSAGES = {
  awaiting_promotion: 'An existing run is ready to promote. Promote or discard it before starting a new Delta run.',
  awaiting_resume: 'A paused or interrupted run must be resumed, promoted, or discarded before starting a new Delta run.',
  already_running: 'A run is already in progress for this scope.',
  run_not_actionable: 'This run is no longer available for that action. Refresh the scope and try again.',
};

const getRunStartErrorMessage = payload => {
  const code = R.path(['json', 'code'], payload)
    || R.path(['json', 'status'], payload)
    || R.path(['json', 'error'], payload);
  if (RUN_START_ERROR_MESSAGES[code]) return RUN_START_ERROR_MESSAGES[code];
  if (typeof payload === 'string' && payload) return payload;
  return 'Unable to start this run. Please try again.';
};

const initialState = {
  scopes: [],
  scopesLoading: false,
  accountCompanyId: null,
  scopesCompanyId: null,
  currentScope: null,
  ingests: [],
  ingestsLoading: false,
  ingestPartnersById: {},
  ingestEmailsById: {},
  ingesting: false,
  assigning: false,
  assignResults: null,
  creatingScope: false,
  scopeAutomationSaving: false,
  runStarting: false,
  runStartError: null,
  deltaSyncStarting: false,
  batchPromotionStarting: false,
  batchPromotionPolling: false,
  batchPromotionStatus: null,
  batchPromotionError: null,
  deltaSyncPolling: false,
  deltaSyncOptimisticUntil: 0,
  // Set when client-side polling gives up while the server still reported the
  // run in progress. This does NOT mean the run finished — only that we stopped
  // watching closely. Cleared as soon as we get a fresh, trusted status read.
  deltaSyncPollTimedOut: false,
  deltaSyncStatus: null,
  deltaSyncNextWindow: null,
  deltaSyncStatusLoading: false,
  stopping: false,
  forceStopping: false,
  promoting: false,
  discarding: false,
  appKeys: [],
  appKeysLoading: false,
  runDetail: null,
  runDetailLoading: false,
  runDetailOpen: false,
  runDetailError: null,
  runDetailRunId: null,
  runDetailRequestKey: null,
  artifactFiles: [],
  artifactContent: null,
  artifactContentName: null,
  artifactLoading: false,
  artifactRequestKey: null,
  artifactContentRequestKey: null,
  addAssociationPending: null,
};

const reducer = (state = initialState, { type, payload, args }) => ({
  ...state,
  ...(() => {
    switch (type) {
      case '@app/GET_ACCOUNT_INFO_SUCCESS': {
        const nextCompanyId = R.path(['company', 'companyId'], payload);
        if (typeof nextCompanyId !== 'string' || !nextCompanyId) return {};
        const currentContextScope = R.path(['currentScope', 'contextScope'], state);
        const currentCompanyId = typeof currentContextScope === 'string'
          ? currentContextScope.split('/')[0]
          : state.scopesCompanyId;
        if (currentCompanyId === nextCompanyId) {
          return { accountCompanyId: nextCompanyId, scopesCompanyId: nextCompanyId };
        }
        return {
          ...initialState,
          accountCompanyId: nextCompanyId,
          scopesCompanyId: nextCompanyId,
        };
      }
      case 'gondola_scopes_STARTED': {
        const requestCompanyId = typeof R.path([0], args) === 'string' ? R.path([0], args) : null;
        if (requestCompanyId && state.accountCompanyId
            && requestCompanyId !== state.accountCompanyId) return {};
        return {
          scopesLoading: true,
          scopesCompanyId: requestCompanyId || state.scopesCompanyId,
        };
      }
      case 'gondola_scopes_SUCCESS': {
        const responseCompanyId = typeof R.path([0], args) === 'string' ? R.path([0], args) : null;
        if (responseCompanyId && state.accountCompanyId
            && responseCompanyId !== state.accountCompanyId) return {};
        if (responseCompanyId && state.scopesCompanyId && responseCompanyId !== state.scopesCompanyId) return {};
        return {
          scopesLoading: false,
          scopesCompanyId: responseCompanyId || state.scopesCompanyId,
          scopes: R.pathOr([], ['scopes'], payload),
        };
      }
      case 'gondola_scopes_ERROR': {
        const responseCompanyId = typeof R.path([0], args) === 'string' ? R.path([0], args) : null;
        if (responseCompanyId && state.accountCompanyId
            && responseCompanyId !== state.accountCompanyId) return {};
        if (responseCompanyId && state.scopesCompanyId && responseCompanyId !== state.scopesCompanyId) return {};
        return { scopesLoading: false };
      }
      case 'gondola_enterScope':
        return {
          runStartError: null,
          currentScope: {
            ...EMPTY_CURRENT_SCOPE,
            scopeId: payload.scopeId,
            contextScope: payload.contextScope || null,
          },
        };
      case 'gondola_leaveScope':
        return { currentScope: null, runStartError: null };
      case 'gondola_saveScopeAutomation_STARTED':
        return { scopeAutomationSaving: true };
      case 'gondola_saveScopeAutomation_SUCCESS':
      case 'gondola_saveScopeAutomation_ERROR':
        return { scopeAutomationSaving: false };
      case 'gondola_scopeDetail_STARTED':
        if (!state.currentScope) return {};
        if (args && args[0] && args[0] !== state.currentScope.scopeId) return {};
        if (shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, detailLoading: true } };
      case 'gondola_scopeDetail_SUCCESS': {
        if (!state.currentScope) return {};
        if (args && args[0] && args[0] !== state.currentScope.scopeId) return {};
        if (shouldRejectScopeAction(state, args)) return {};
        const responseScope = R.prop('scope', payload);
        const scope = responseScope && typeof responseScope === 'object'
          ? responseScope
          : (payload || {});
        return {
          currentScope: {
            ...state.currentScope,
            ...scope,
            scopeId: state.currentScope.scopeId,
            contextScope: scope.contextScope || state.currentScope.contextScope,
            detailLoading: false,
          },
        };
      }
      case 'gondola_scopeDetail_ERROR':
        if (!state.currentScope) return {};
        if (args && args[0] && args[0] !== state.currentScope.scopeId) return {};
        if (shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, detailLoading: false } };
      case 'gondola_scopeMapping_STARTED':
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, associationsLoading: true } };
      case 'gondola_scopeMapping_SUCCESS': {
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        const mapping = R.prop('mapping', payload) || payload || {};
        return {
          currentScope: {
            ...state.currentScope,
            associationsLoading: false,
            associations: R.propOr([], 'associations', mapping),
            ti2s: R.propOr([], 'ti2s', mapping),
            ti2Source: R.propOr(null, 'ti2Source', mapping),
          },
        };
      }
      case 'gondola_scopeMapping_ERROR':
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, associationsLoading: false } };
      case 'gondola_scopeRuns_STARTED':
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, runsLoading: true } };
      case 'gondola_scopeRuns_SUCCESS': {
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        const nextRuns = R.propOr([], 'runs', payload);
        const cursor = R.path([2], args);
        const runs = cursor
          ? [...state.currentScope.runs, ...nextRuns]
            .filter((run, index, rows) => rows.findIndex(row => row.runId === run.runId) === index)
          : nextRuns;
        return {
          currentScope: {
            ...state.currentScope,
            runsLoading: false,
            runs,
            runsNextCursor: R.propOr(null, 'nextCursor', payload),
          },
        };
      }
      case 'gondola_scopeRuns_ERROR':
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, runsLoading: false } };
      case 'gondola_scopeFiles_STARTED':
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, filesLoading: true } };
      case 'gondola_scopeFiles_SUCCESS':
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        return {
          currentScope: {
            ...state.currentScope,
            filesLoading: false,
            files: R.propOr([], 'files', payload),
          },
        };
      case 'gondola_scopeFiles_ERROR':
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, filesLoading: false } };
      case 'gondola_scopeCounts_STARTED':
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, countsLoading: true } };
      case 'gondola_scopeCounts_SUCCESS': {
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        const counts = R.prop('counts', payload) || payload || {};
        return {
          currentScope: {
            ...state.currentScope,
            countsLoading: false,
            pairCount: R.propOr(0, 'pairCount', counts),
            bookingCount: R.propOr(R.propOr(0, 'pairCount', counts), 'bookingCount', counts),
            caseCount: R.propOr(0, 'caseCount', counts),
            processedCount: R.propOr(0, 'processedCount', counts),
            deltaEligibleCount: R.propOr(0, 'deltaEligibleCount', counts),
            serviceRulesEligibleCount: R.propOr(0, 'serviceRulesEligibleCount', counts),
          },
        };
      }
      case 'gondola_scopeCounts_ERROR':
        if (!state.currentScope || shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, countsLoading: false } };
      case 'gondola_startIngestion':
        return { ingesting: true };
      case 'gondola_startIngestion_SUCCESS':
      case 'gondola_startIngestion_ERROR':
      case 'gondola_startIngestion_CANCELLED':
        return { ingesting: false };
      case 'gondola_ingests_STARTED':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { ingestsLoading: true };
      case 'gondola_ingests_SUCCESS':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return {
          ingestsLoading: false,
          ingests: Array.isArray(R.prop('ingests', payload)) ? payload.ingests : [],
        };
      case 'gondola_ingests_ERROR':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { ingestsLoading: false };
      case 'gondola_ingestPartners_STARTED': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.prop('ingestId', payload);
        if (!ingestId) return {};
        const current = state.ingestPartnersById[ingestId] || {};
        const requestId = R.prop('requestId', payload);
        return {
          ingestPartnersById: {
            ...state.ingestPartnersById,
            [ingestId]: {
              ...current,
              loading: true,
              error: null,
              ...(requestId === undefined ? {} : { requestId }),
            },
          },
        };
      }
      case 'gondola_ingestPartners_SUCCESS': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.prop('ingestId', payload);
        if (!ingestId) return {};
        const current = state.ingestPartnersById[ingestId] || {};
        const requestId = R.prop('requestId', payload);
        if (requestId !== undefined && requestId !== current.requestId) return {};
        return {
          ingestPartnersById: {
            ...state.ingestPartnersById,
            [ingestId]: {
              ...current,
              loading: false,
              error: null,
              partners: Array.isArray(R.path(['response', 'partners'], payload))
                ? payload.response.partners
                : [],
              caseAssociationUnresolved: Array.isArray(R.path(['response', 'caseAssociationUnresolved'], payload))
                ? payload.response.caseAssociationUnresolved
                : [],
              caseAssignments: Array.isArray(R.path(['response', 'caseAssignments'], payload))
                ? payload.response.caseAssignments
                : (current.caseAssignments || []),
              assignmentOperationError: R.has('assignmentOperationError', payload.response || {})
                ? payload.response.assignmentOperationError : (current.assignmentOperationError || null),
              ...(requestId === undefined ? {} : { requestId }),
            },
          },
        };
      }
      case 'gondola_ingestPartners_ERROR': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.prop('ingestId', payload);
        if (!ingestId) return {};
        const current = state.ingestPartnersById[ingestId] || {};
        const requestId = R.prop('requestId', payload);
        if (requestId !== undefined && requestId !== current.requestId) return {};
        return {
          ingestPartnersById: {
            ...state.ingestPartnersById,
            [ingestId]: {
              ...current,
              loading: false,
              error: R.prop('error', payload) || 'error',
              ...(requestId === undefined ? {} : { requestId }),
            },
          },
        };
      }
      case 'gondola_ingestEmails_STARTED': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.prop('ingestId', payload);
        if (!ingestId) return {};
        const current = state.ingestEmailsById[ingestId] || {};
        const requestId = R.prop('requestId', payload);
        return {
          ingestEmailsById: {
            ...state.ingestEmailsById,
            [ingestId]: {
              emails: [],
              ti2s: [],
              detailByEmailId: {},
              searchByEmailId: {},
              selectByEmailId: {},
              ...current,
              loading: true,
              error: null,
              ...(requestId === undefined ? {} : { requestId }),
            },
          },
        };
      }
      case 'gondola_ingestEmails_SUCCESS': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.prop('ingestId', payload);
        if (!ingestId) return {};
        const current = state.ingestEmailsById[ingestId] || {};
        const requestId = R.prop('requestId', payload);
        if (requestId !== undefined && requestId !== current.requestId) return {};
        const response = R.propOr({}, 'response', payload);
        return {
          ingestEmailsById: {
            ...state.ingestEmailsById,
            [ingestId]: {
              loading: false,
              error: null,
              emails: Array.isArray(response.emails) ? response.emails : [],
              ti2s: Array.isArray(response.ti2s) ? response.ti2s : [],
              detailByEmailId: current.detailByEmailId || {},
              searchByEmailId: current.searchByEmailId || {},
              selectByEmailId: current.selectByEmailId || {},
              ...(requestId === undefined ? {} : { requestId }),
            },
          },
        };
      }
      case 'gondola_ingestEmails_ERROR': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.prop('ingestId', payload);
        if (!ingestId) return {};
        const current = state.ingestEmailsById[ingestId] || {};
        const requestId = R.prop('requestId', payload);
        if (requestId !== undefined && requestId !== current.requestId) return {};
        return {
          ingestEmailsById: {
            ...state.ingestEmailsById,
            [ingestId]: {
              ...current,
              loading: false,
              error: R.prop('error', payload) || 'error',
              ...(requestId === undefined ? {} : { requestId }),
            },
          },
        };
      }
      case 'gondola_ingestEmailDetail_STARTED':
      case 'gondola_ingestEmailDetail_SUCCESS':
      case 'gondola_ingestEmailDetail_ERROR':
      case 'gondola_ingestEmailDetail_CANCELLED': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.prop('ingestId', payload);
        const emailId = R.prop('emailId', payload);
        if (!ingestId || !emailId) return {};
        const ingestBucket = state.ingestEmailsById[ingestId] || {
          emails: [], ti2s: [], detailByEmailId: {}, searchByEmailId: {}, selectByEmailId: {},
        };
        const currentDetailState = R.pathOr({}, ['detailByEmailId', emailId], ingestBucket);
        const requestId = R.prop('requestId', payload);
        if (!type.endsWith('_STARTED')
          && Object.prototype.hasOwnProperty.call(currentDetailState, 'requestId')
          && requestId !== currentDetailState.requestId) return {};
        let detailState;
        if (type.endsWith('_STARTED')) {
          detailState = {
            loading: true,
            error: null,
            detail: null,
            ...(requestId === undefined ? {} : { requestId }),
          };
        } else if (type.endsWith('_CANCELLED')) {
          detailState = {
            ...currentDetailState,
            loading: false,
          };
        } else if (type.endsWith('_ERROR')) {
          detailState = {
            ...currentDetailState,
            loading: false,
            error: R.prop('error', payload) || 'error',
          };
        } else {
          detailState = {
            ...currentDetailState,
            loading: false,
            error: null,
            detail: R.propOr({}, 'response', payload),
          };
        }
        return {
          ingestEmailsById: {
            ...state.ingestEmailsById,
            [ingestId]: {
              ...ingestBucket,
              detailByEmailId: {
                ...(ingestBucket.detailByEmailId || {}),
                [emailId]: detailState,
              },
            },
          },
        };
      }
      case 'gondola_ingestEmailSearch_STARTED':
      case 'gondola_ingestEmailSearch_SUCCESS':
      case 'gondola_ingestEmailSearch_ERROR':
      case 'gondola_ingestEmailSearch_CANCELLED': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.prop('ingestId', payload);
        const emailId = R.prop('emailId', payload);
        if (!ingestId || !emailId) return {};
        const ingestBucket = state.ingestEmailsById[ingestId] || {
          emails: [], ti2s: [], searchByEmailId: {}, selectByEmailId: {},
        };
        const currentSearchState = R.pathOr({}, ['searchByEmailId', emailId], ingestBucket);
        const requestId = R.prop('requestId', payload);
        if (!type.endsWith('_STARTED')
          && Object.prototype.hasOwnProperty.call(currentSearchState, 'requestId')
          && requestId !== currentSearchState.requestId) return {};
        let searchState;
        if (type.endsWith('_STARTED')) {
          searchState = {
            loading: true,
            error: null,
            candidates: [],
            strategy: null,
            ...(requestId === undefined ? {} : { requestId }),
          };
        } else if (type.endsWith('_CANCELLED')) {
          searchState = {
            ...currentSearchState,
            loading: false,
          };
        } else if (type.endsWith('_ERROR')) {
          searchState = {
            ...currentSearchState,
            loading: false,
            error: R.prop('error', payload) || 'error',
          };
        } else {
          const response = R.propOr({}, 'response', payload);
          searchState = {
            ...currentSearchState,
            loading: false,
            error: null,
            candidates: Array.isArray(response.candidates) ? response.candidates : [],
            strategy: typeof response.strategy === 'string' ? response.strategy : null,
          };
        }
        return {
          ingestEmailsById: {
            ...state.ingestEmailsById,
            [ingestId]: {
              ...ingestBucket,
              searchByEmailId: {
                ...(ingestBucket.searchByEmailId || {}),
                [emailId]: searchState,
              },
            },
          },
        };
      }
      case 'gondola_ingestEmailSelect_STARTED':
      case 'gondola_ingestEmailSelect_SUCCESS':
      case 'gondola_ingestEmailSelect_ERROR':
      case 'gondola_ingestEmailSelect_CANCELLED':
      case 'gondola_ingestEmailSelect_MONITORING_ERROR':
      case 'gondola_ingestEmailSelect_MONITORING_DONE': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.prop('ingestId', payload);
        const emailId = R.prop('emailId', payload);
        if (!ingestId || !emailId) return {};
        const ingestBucket = state.ingestEmailsById[ingestId] || {
          emails: [], ti2s: [], searchByEmailId: {}, selectByEmailId: {},
        };
        const currentSelectState = R.pathOr({}, ['selectByEmailId', emailId], ingestBucket);
        const requestId = R.prop('requestId', payload);
        if (!type.endsWith('_STARTED')
          && Object.prototype.hasOwnProperty.call(currentSelectState, 'requestId')
          && requestId !== currentSelectState.requestId) return {};
        let selectState;
        if (type.endsWith('_STARTED')) {
          selectState = {
            loading: true,
            monitoring: false,
            error: null,
            result: null,
            ...(requestId === undefined ? {} : { requestId }),
          };
        } else if (type.endsWith('_CANCELLED')) {
          selectState = {
            ...currentSelectState,
            loading: false,
            monitoring: false,
          };
        } else if (type.endsWith('_MONITORING_ERROR')) {
          selectState = {
            ...currentSelectState,
            loading: false,
            monitoring: true,
            error: R.prop('error', payload) || 'Unable to monitor ingestion',
            result: {
              ...R.propOr({}, 'result', currentSelectState),
              status: 'monitoring_unknown',
            },
          };
        } else if (type.endsWith('_ERROR')) {
          selectState = {
            ...currentSelectState,
            loading: false,
            monitoring: false,
            error: R.prop('error', payload) || 'error',
            result: null,
          };
        } else if (type.endsWith('_MONITORING_DONE')) {
          const terminalStatus = R.prop('terminalStatus', payload);
          selectState = {
            ...currentSelectState,
            loading: false,
            monitoring: false,
            error: null,
            result: {
              ...R.propOr({}, 'result', currentSelectState),
              status: terminalStatus || 'completed',
            },
          };
        } else {
          const response = R.propOr({}, 'response', payload);
          selectState = {
            ...currentSelectState,
            loading: false,
            monitoring: R.prop('status', response) === 'accepted',
            error: null,
            result: response,
          };
        }
        return {
          ingestEmailsById: {
            ...state.ingestEmailsById,
            [ingestId]: {
              ...ingestBucket,
              selectByEmailId: {
                ...(ingestBucket.selectByEmailId || {}),
                [emailId]: selectState,
              },
            },
          },
        };
      }
      case 'gondola_assign_STARTED':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { assigning: true, assignResults: null };
      case 'gondola_assign_SUCCESS': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.path([0], args);
        const current = state.ingestPartnersById[ingestId] || {};
        return {
          assigning: false,
          assignResults: Array.isArray(R.prop('results', payload)) ? payload.results : [],
          ...(typeof ingestId !== 'string' ? {} : {
            ingestPartnersById: {
              ...state.ingestPartnersById,
              [ingestId]: {
                ...current,
                caseAssignments: Array.isArray(R.prop('caseAssignments', payload))
                  ? payload.caseAssignments : (current.caseAssignments || []),
                assignmentOperationError: R.prop('assignmentOperationError', payload) || null,
              },
            },
          }),
        };
      }
      case 'gondola_assign_ERROR': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const ingestId = R.path([0], args);
        return {
          assigning: false,
          ...(typeof ingestId !== 'string' ? {} : {
            ingestPartnersById: {
              ...state.ingestPartnersById,
              [ingestId]: {
                ...state.ingestPartnersById[ingestId],
                assignmentOperationError: {
                  error: typeof payload === 'string' ? payload : 'Unable to complete assignment.',
                },
              },
            },
          }),
        };
      }
      case 'gondola_createScope_STARTED':
        return { creatingScope: true };
      case 'gondola_createScope_SUCCESS':
        return { creatingScope: false };
      case 'gondola_createScope_ERROR':
        return { creatingScope: false };
      case 'gondola_startRun_STARTED':
        return {
          runStarting: true,
          runStartError: null,
        };
      case 'gondola_startRun_SUCCESS': {
        return {
          runStarting: false,
          runStartError: null,
        };
      }
      case 'gondola_runPollingStarted': {
        const runId = R.prop('runId', payload);
        const celeryJobId = R.prop('celeryJobId', payload);
        const contextScope = R.prop('contextScope', payload);
        if (!state.currentScope || !isMatchingCurrentScope(state, contextScope) || !celeryJobId) return {};
        return {
          currentScope: {
            ...state.currentScope,
            liveRun: { ...(runId ? { runId } : {}), celeryJobId, status: 'pending' },
          },
        };
      }
      case 'gondola_startRun_ERROR':
        if (shouldRejectScopeAction(state, args)) return { runStarting: false };
        return {
          runStarting: false,
          runStartError: getRunStartErrorMessage(payload),
        };
      case 'gondola_batchPromotionRun_STARTED':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { batchPromotionStarting: true, batchPromotionError: null };
      case 'gondola_batchPromotionRun_SUCCESS':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { batchPromotionStarting: false };
      case 'gondola_batchPromotionRun_ERROR':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { batchPromotionStarting: false, batchPromotionError: 'Could not start batch promotion. Please try again.' };
      case 'gondola_batchPromotionPoll_STARTED':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { batchPromotionPolling: true, batchPromotionError: null };
      case 'gondola_batchPromotionPoll_ENDED':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { batchPromotionPolling: false };
      case 'gondola_batchPromotionPoll_TIMEOUT':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { batchPromotionError: 'Status monitoring stopped. Refresh status to check whether the batch has finished.' };
      case 'gondola_batchPromotionStatus_SUCCESS':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { batchPromotionStatus: R.propOr(null, 'status', payload), batchPromotionError: null };
      case 'gondola_batchPromotionStatus_ERROR':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { batchPromotionError: 'Could not load batch promotion status. Refresh status to try again.' };
      case 'gondola_deltaSyncRun_STARTED': {
        const startedAt = new Date().toISOString();
        return {
          deltaSyncStarting: true,
          // Bounds how long we'll ignore a lagging "not running yet" status response
          // after we've just told the server to start (see deltaSyncStatus_SUCCESS below).
          // Matches the saga's own await-start grace window.
          deltaSyncOptimisticUntil: Date.now() + 30e3,
          deltaSyncPollTimedOut: false,
          deltaSyncStatus: {
            ...(state.deltaSyncStatus || {}),
            inProgress: true,
            progress: undefined,
            lastStartedAt: startedAt,
            lockStartedAt: startedAt,
          },
        };
      }
      case 'gondola_deltaSyncRun_SUCCESS':
        return { deltaSyncStarting: false };
      case 'gondola_deltaSyncRun_ERROR':
        return {
          deltaSyncStarting: false,
          deltaSyncOptimisticUntil: 0,
          deltaSyncPollTimedOut: false,
          deltaSyncStatus: state.deltaSyncStatus
            ? { ...state.deltaSyncStatus, inProgress: false }
            : null,
        };
      case 'gondola_deltaSyncPoll_STARTED':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { deltaSyncPolling: true };
      case 'gondola_deltaSyncPoll_ENDED':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { deltaSyncPolling: false };
      case 'gondola_deltaSyncPoll_TIMEOUT':
        return {
          deltaSyncPolling: false,
          deltaSyncOptimisticUntil: 0,
          // The client gave up waiting, but the server never confirmed the run
          // finished — leave deltaSyncStatus otherwise untouched (its progress is
          // still the most recent live data we have) and flag it as ambiguous
          // rather than pretending we know the run is idle.
          deltaSyncPollTimedOut: true,
          deltaSyncStatus: state.deltaSyncStatus
            ? { ...state.deltaSyncStatus, inProgress: false }
            : null,
        };
      case 'gondola_deltaSyncStatus_STARTED':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { deltaSyncStatusLoading: true };
      case 'gondola_deltaSyncStatus_SUCCESS': {
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        const nextStatus = R.propOr(null, 'status', payload);
        const nextWindow = R.propOr(null, 'nextWindow', payload);
        const nextInProgress = Boolean(R.prop('inProgress', nextStatus));
        if (nextInProgress) {
          return {
            deltaSyncStatusLoading: false,
            deltaSyncStatus: nextStatus,
            deltaSyncNextWindow: nextWindow,
            deltaSyncOptimisticUntil: 0,
            deltaSyncPollTimedOut: false,
          };
        }
        // A response reporting inProgress: false can lag behind a run we just
        // triggered (server hasn't picked it up yet). Ignore it only within a
        // bounded grace window so a sync that finishes very quickly (faster than
        // the server confirms it started) doesn't get stuck showing "running"
        // forever — after the window, trust the server even if never confirmed.
        if (state.deltaSyncPolling && Date.now() < state.deltaSyncOptimisticUntil) {
          return { deltaSyncStatusLoading: false };
        }
        // A trusted, fresh "not in progress" read (i.e. not one we overrode
        // ourselves on timeout) means the ambiguity is resolved either way.
        return {
          deltaSyncStatusLoading: false,
          deltaSyncStatus: nextStatus,
          deltaSyncNextWindow: nextWindow,
          deltaSyncOptimisticUntil: 0,
          deltaSyncPollTimedOut: false,
        };
      }
      case 'gondola_deltaSyncStatus_ERROR':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { deltaSyncStatusLoading: false };
      case 'gondola_stopRun_STARTED':
        return { stopping: true };
      case 'gondola_stopRun_SUCCESS':
        return { stopping: false };
      case 'gondola_stopRun_ERROR':
        return { stopping: false };
      case 'gondola_forceStopRun_STARTED':
        return { forceStopping: true };
      case 'gondola_forceStopRun_SUCCESS':
      case 'gondola_forceStopRun_ERROR':
        return { forceStopping: false };
      case 'gondola_promoteRun_STARTED':
        return { promoting: true, runStartError: null };
      case 'gondola_promoteRun_SUCCESS':
      case 'gondola_promoteRun_ERROR':
        return { promoting: false };
      case 'gondola_discardRun_STARTED':
        return { discarding: true, runStartError: null };
      case 'gondola_discardRun_SUCCESS':
      case 'gondola_discardRun_ERROR':
        return { discarding: false };
      case 'gondola_appKeys_STARTED':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { appKeysLoading: true };
      case 'gondola_appKeys_SUCCESS':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { appKeysLoading: false, appKeys: R.propOr([], 'userAppKeys', payload) };
      case 'gondola_appKeys_ERROR':
        if (shouldRejectCompanyAction(state, args, payload)) return {};
        return { appKeysLoading: false };
      case 'gondola_runDetailOpen':
        return {
          runDetailOpen: true,
          runDetail: null,
          runDetailLoading: true,
          runDetailError: null,
          runDetailRunId: null,
        };
      case 'gondola_runDetailClose':
        return {
          runDetailOpen: false,
          runDetail: null,
          runDetailLoading: false,
          runDetailError: null,
          runDetailRunId: null,
          runDetailRequestKey: null,
          artifactFiles: [],
          artifactContent: null,
          artifactContentName: null,
          artifactLoading: false,
          artifactRequestKey: null,
          artifactContentRequestKey: null,
        };
      case 'gondola_runDetail_STARTED':
        if (shouldRejectScopeAction(state, args)) return {};
        return {
          runDetailLoading: true,
          runDetail: null,
          runDetailError: null,
          runDetailRunId: typeof R.path([1], args) === 'string' ? R.path([1], args) : null,
          runDetailRequestKey: getScopeRequestKey(args, 2),
        };
      case 'gondola_runDetail_SUCCESS':
        if (shouldRejectScopeAction(state, args)) return {};
        if (getScopeRequestKey(args, 2)
            && getScopeRequestKey(args, 2) !== state.runDetailRequestKey) return {};
        return { runDetailLoading: false, runDetail: payload, runDetailError: null };
      case 'gondola_runDetail_ERROR':
        if (shouldRejectScopeAction(state, args)) return {};
        if (getScopeRequestKey(args, 2)
            && getScopeRequestKey(args, 2) !== state.runDetailRequestKey) return {};
        return {
          runDetailLoading: false,
          runDetailError: 'Unable to load run details. Please try again.',
        };
      case 'gondola_artifacts_STARTED':
        if (shouldRejectScopeAction(state, args)) return {};
        return {
          artifactLoading: true,
          artifactFiles: [],
          artifactContent: null,
          artifactContentName: null,
          artifactRequestKey: getScopeRequestKey(args, 4),
          artifactContentRequestKey: null,
        };
      case 'gondola_artifacts_SUCCESS':
        if (shouldRejectScopeAction(state, args)) return {};
        if (getScopeRequestKey(args, 4)
            && getScopeRequestKey(args, 4) !== state.artifactRequestKey) return {};
        return { artifactLoading: false, artifactFiles: R.propOr([], 'files', payload) };
      case 'gondola_artifacts_ERROR':
        if (shouldRejectScopeAction(state, args)) return {};
        if (getScopeRequestKey(args, 4)
            && getScopeRequestKey(args, 4) !== state.artifactRequestKey) return {};
        return { artifactLoading: false };
      case 'gondola_artifactContent_STARTED':
        if (shouldRejectScopeAction(state, args)) return {};
        return {
          artifactLoading: true,
          artifactContent: null,
          artifactContentRequestKey: getScopeRequestKey(args, 4),
        };
      case 'gondola_artifactContent_SUCCESS':
        if (shouldRejectScopeAction(state, args)) return {};
        if (getScopeRequestKey(args, 4)
            && getScopeRequestKey(args, 4) !== state.artifactContentRequestKey) return {};
        return { artifactLoading: false, artifactContent: R.propOr(null, 'content', payload), artifactContentName: R.propOr(null, 'fileName', payload) };
      case 'gondola_artifactContent_ERROR':
        if (shouldRejectScopeAction(state, args)) return {};
        if (getScopeRequestKey(args, 4)
            && getScopeRequestKey(args, 4) !== state.artifactContentRequestKey) return {};
        return { artifactLoading: false };
      case 'gondola_scopeFile_STARTED':
        if (!state.currentScope) return {};
        if (shouldRejectScopeAction(state, args)) return {};
        return {
          currentScope: {
            ...state.currentScope,
            selectedFileLoading: true,
            selectedFileName: R.pathOr(null, [1], args),
            selectedFileContent: null,
            selectedFileDownloadUrl: null,
            selectedFileS3Path: null,
            selectedFileAccess: null,
            selectedFileSize: null,
            scopeFileRequestKey: getScopeRequestKey(args, 2),
          },
        };
      case 'gondola_scopeFile_SUCCESS':
        if (!state.currentScope) return {};
        if (shouldRejectScopeAction(state, args)) return {};
        if (getScopeRequestKey(args, 2)
            && getScopeRequestKey(args, 2) !== state.currentScope.scopeFileRequestKey) return {};
        return {
          currentScope: {
            ...state.currentScope,
            selectedFileLoading: false,
            selectedFileContent: R.propOr(null, 'content', payload),
            selectedFileName: R.propOr(null, 'fileName', payload),
            selectedFileDownloadUrl: R.propOr(null, 'downloadUrl', payload),
            selectedFileS3Path: R.propOr(null, 's3Path', payload),
            selectedFileAccess: R.propOr(null, 'access', payload),
            selectedFileSize: R.propOr(null, 'size', payload),
          },
        };
      case 'gondola_scopeFile_ERROR':
        if (!state.currentScope) return {};
        if (shouldRejectScopeAction(state, args)) return {};
        if (getScopeRequestKey(args, 2)
            && getScopeRequestKey(args, 2) !== state.currentScope.scopeFileRequestKey) return {};
        return { currentScope: { ...state.currentScope, selectedFileLoading: false } };
      case 'gondola_scopeFileSave_STARTED':
        if (!state.currentScope) return {};
        if (shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, savingFile: true } };
      case 'gondola_scopeFileSave_SUCCESS':
        if (!state.currentScope) return {};
        if (shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, savingFile: false } };
      case 'gondola_scopeFileSave_ERROR':
        if (!state.currentScope) return {};
        if (shouldRejectScopeAction(state, args)) return {};
        return { currentScope: { ...state.currentScope, savingFile: false } };
      case 'gondola_scopeFileClear':
        if (!state.currentScope) return {};
        return {
          currentScope: {
            ...state.currentScope,
            selectedFileName: null,
            selectedFileContent: null,
            selectedFileDownloadUrl: null,
            selectedFileS3Path: null,
            selectedFileAccess: null,
            selectedFileSize: null,
            scopeFileRequestKey: null,
          },
        };
      case 'gondola_addAssociation_NEEDS_CONFIRM':
        return { addAssociationPending: payload };
      case 'gondola_addAssociation_SUCCESS':
        return { addAssociationPending: null };
      case 'gondola_addAssociation_ERROR':
        return { addAssociationPending: null };
      case 'gondola_clearAddAssociationConfirm':
        return { addAssociationPending: null };
      case 'gondola_jobProgress': {
        if (!state.currentScope) return {};
        const jobResp = R.propOr({}, 'jobResp', payload);
        const celeryJobId = R.prop('celeryJobId', payload);
        const contextScope = R.prop('contextScope', payload);
        if (contextScope && !isMatchingCurrentScope(state, contextScope)) return {};
        const existingLiveRun = state.currentScope.liveRun || {};
        if (celeryJobId
            && existingLiveRun.celeryJobId
            && celeryJobId !== existingLiveRun.celeryJobId) {
          return {};
        }
        const log = sanitizeJobLog(jobResp.log);
        const progress = parseProgressMarker(log) || {};
        const receivedStatus = sanitizeJobText(jobResp.status, 64);
        const unsupportedStatus = Boolean(receivedStatus && !SUPPORTED_JOB_STATUSES.has(receivedStatus));
        const status = unsupportedStatus ? 'failure' : receivedStatus;
        const rawStatus = sanitizeJobText(jobResp.rawStatus, 64);
        const resultDisposition = sanitizeJobText(
          jobResp.resultDisposition || R.path(['metadata', 'resultDisposition'], jobResp),
          64,
        );
        const info = unsupportedStatus
          ? 'Unexpected job status received'
          : sanitizeJobText(jobResp.info);
        const alertType = SAFE_JOB_ALERT_TYPES.has(jobResp.alertType) ? jobResp.alertType : null;
        return {
          currentScope: {
            ...state.currentScope,
            liveRun: {
              ...existingLiveRun,
              ...(celeryJobId ? { celeryJobId } : {}),
              log,
              ...progress,
              status: status || R.propOr('pending', 'status', existingLiveRun),
              rawStatus,
              resultDisposition,
              info,
              alertType,
              requiresAttention: unsupportedStatus || jobResp.requiresAttention === true,
            },
          },
        };
      }
      default:
        return {};
    }
  })(),
});

export default reducer;
